/**
 * Hook for fetching pending moderation counts
 * Used for badge indicator on moderation tab
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/useUserRole';

interface ModerationPendingCounts {
  pendingListings: number;
  pendingReports: number;
  pendingChatReports: number;
  total: number;
}

const STALE_TIME = 60 * 1000; // 1 minute
const REFETCH_INTERVAL = 2 * 60 * 1000; // 2 minutes

export function useModerationPendingCount() {
  const { isModerator, isLoading: isRoleLoading } = useUserRole();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['moderation-pending-count'],
    queryFn: async (): Promise<ModerationPendingCounts> => {
      // Fetch pending listings count
      const { count: pendingListings, error: listingsError } = await supabase
        .from('listings')
        .select('id', { count: 'exact', head: true })
        .eq('moderation_status', 'pending')
        .is('deleted_at', null);

      if (listingsError) throw listingsError;

      // Fetch pending reports (moderation_checks with needs_review)
      const { count: pendingReports, error: reportsError } = await supabase
        .from('moderation_checks')
        .select('id', { count: 'exact', head: true })
        .eq('decision', 'needs_review')
        .neq('entity_type', 'chat_message'); // Exclude chat reports

      if (reportsError) throw reportsError;

      // Fetch pending chat reports
      const { count: pendingChatReports, error: chatError } = await supabase
        .from('moderation_checks')
        .select('id', { count: 'exact', head: true })
        .eq('decision', 'needs_review')
        .eq('entity_type', 'chat_message');

      if (chatError) throw chatError;

      const counts = {
        pendingListings: pendingListings || 0,
        pendingReports: pendingReports || 0,
        pendingChatReports: pendingChatReports || 0,
        total: (pendingListings || 0) + (pendingReports || 0) + (pendingChatReports || 0),
      };

      return counts;
    },
    enabled: !isRoleLoading && isModerator,
    staleTime: STALE_TIME,
    gcTime: 5 * 60 * 1000,
    refetchInterval: REFETCH_INTERVAL,
  });

  return {
    counts: data || { pendingListings: 0, pendingReports: 0, pendingChatReports: 0, total: 0 },
    isLoading: isLoading || isRoleLoading,
    error,
    refetch,
    isModerator,
  };
}

export default useModerationPendingCount;
